import { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { IoCallSharp } from "react-icons/io5";
import { MdBathtub, MdBed, MdKitchen, MdMeetingRoom } from "react-icons/md";
import InteriorLeadFormModal from "../../modals/InteriorLeadFormModal";

import interior1 from "../../../assets-user/interior/interior1.png";
import interior2 from "../../../assets-user/interior/interior2.png";
import interior3 from "../../../assets-user/interior/interior3.png";
import interior4 from "../../../assets-user/interior/interior4.jpg";
import interior5 from "../../../assets-user/interior/interior5.jpg";
import interior6 from "../../../assets-user/interior/interior6.jpg";

const placeholderImages = [
  "https://images.unsplash.com/photo-1600210492493-0946911123ea?w=800&h=600&fit=crop",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop",
  "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&h=600&fit=crop",
];

const properties = [
  {
    id: 1,
    title: "Urban Chic 2BHK Interior",
    propertyType: "2 BHK",
    images: [interior1, interior4, interior6, ...placeholderImages],
    amenities: ["Kitchen", "Hall", "Bedroom"],
    cost: "₹18L",
    duration: "5 Months",
    location: "Patia, Bhubaneswar",
  },
  {
    id: 2,
    title: "Modern Minimalist Studio Home",
    propertyType: "Studio",
    images: [interior2, interior5, ...placeholderImages],
    amenities: ["Hall", "Kitchen"],
    cost: "₹12L",
    duration: "3 Months",
    location: "Khandagiri, Bhubaneswar",
  },
  {
    id: 3,
    title: "Elegant 3BHK Family Interior",
    propertyType: "3 BHK",
    images: [interior3, interior6, interior2, ...placeholderImages],
    amenities: ["Bedroom", "Bathroom", "Hall", "Kitchen"],
    cost: "₹25L",
    duration: "7 Months",
    location: "Infocity, Bhubaneswar",
  },
  {
    id: 4,
    title: "Luxury Penthouse Interior",
    propertyType: "Penthouse",
    images: [interior5, interior3, ...placeholderImages],
    amenities: ["Hall", "Bedroom", "Bathroom", "Terrace Lounge"],
    cost: "₹40L",
    duration: "8 Months",
    location: "Chandaka, Bhubaneswar",
  },
  {
    id: 5,
    title: "Contemporary Villa Interior",
    propertyType: "Villa",
    images: [interior4, interior1, interior2, ...placeholderImages],
    amenities: ["Kitchen", "Hall", "Bedroom", "Garden", "Dining"],
    cost: "₹55L",
    duration: "10 Months",
    location: "Pahala, Bhubaneswar",
  },
  {
    id: 6,
    title: "Affordable Compact 1BHK Interior",
    propertyType: "1 BHK",
    images: [interior6, interior1, interior5, ...placeholderImages],
    amenities: ["Bedroom", "Kitchen"],
    cost: "₹10L",
    duration: "2 Months",
    location: "Jaydev Vihar, Bhubaneswar",
  },
];

const InteriorCard = ({ property, idx, setSelectedProperty, setOpenModal }) => {
  const [imgIndex, setImgIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setImgIndex((prev) =>
        prev === property.images.length - 1 ? 0 : prev + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [property.images.length]);

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden cursor-pointer">
      {/* FIXED HEIGHT IMAGE SECTION */}
      <div className="relative w-full h-[220px] sm:h-[240px] md:h-[260px] overflow-hidden group">
        <img
          src={property.images[imgIndex]}
          alt={property.title}
          className="w-full h-full object-cover"
        />

        {/* Navigation Buttons */}
        {property.images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setImgIndex((prev) =>
                  prev === 0 ? property.images.length - 1 : prev - 1
                );
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
            >
              <FaChevronLeft />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setImgIndex((prev) =>
                  prev === property.images.length - 1 ? 0 : prev + 1
                );
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
            >
              <FaChevronRight />
            </button>
          </>
        )}
      </div>

      {/* CONTENT */}
      <div className="px-4 py-3">
        <h3 className="text-lg font-medium font-[Poppins] text-[#031722] mb-2">
          {property.title}
        </h3>

        {/* Amenities */}
        <div className="flex flex-wrap gap-4 mb-4 border rounded-lg p-2 min-h-[60px]">
          {property.amenities.map((amenity, i) => (
            <div
              key={i}
              className="text-sm text-[#6A6A6A] font-[Poppins] flex items-center gap-2"
            >
              {amenity === "Kitchen" && <MdKitchen className="text-red-500" />}
              {amenity === "Bathroom" && (
                <MdBathtub className="text-blue-500" />
              )}
              {amenity === "Hall" && (
                <MdMeetingRoom className="text-green-500" />
              )}
              {amenity === "Bedroom" && <MdBed className="text-purple-500" />}
              <span>{amenity}</span>
            </div>
          ))}
        </div>

        {/* Cost + Duration */}
        <div className="flex justify-between items-center mb-4 text-sm">
          <p className="text-gray-600">
            Project Cost:
            <span className="font-semibold text-[#0C3861] ml-1">
              {property.cost}
            </span>
          </p>
          <p className="text-gray-600">
            Duration:
            <span className="font-semibold ml-1">{property.duration}</span>
          </p>
        </div>

        {/* Contact Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedProperty(property);
            setOpenModal(true);
          }}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 rounded-lg flex items-center justify-center gap-2"
        >
          <IoCallSharp size={18} />
          Contact Builder
        </button>
      </div>
    </div>
  );
};

const InteriorDesign = () => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  return (
    <div className="px-4 sm:px-6 md:px-8 lg:px-10 py-6">
      {/* GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {properties.map((property, idx) => (
          <InteriorCard
            key={property.id}
            property={property}
            idx={idx}
            setOpenModal={setOpenModal}
            setSelectedProperty={setSelectedProperty}
          />
        ))}
      </div>

      {/* MODAL */}
      <InteriorLeadFormModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        property={selectedProperty}
      />
    </div>
  );
};

export default InteriorDesign;
